import React, { useEffect, useState } from 'react';
import { Form, Select, Tooltip, Typography } from 'antd';
import { get_batch_purposes } from '@/utils/api';

const { Text } = Typography;

/**
 * CPV-01 DD-CPV-21 · what this batch is for — optional at issue (2026-10-07; `required` marks it).
 *
 * The options come from the server (`GET /bp/batch-purposes/`), for this record, plant and market:
 * each purpose carries the server's own answer and its reason. Allowed ones can be chosen; the rest
 * are shown disabled with the server's reason. Nothing here decides which purposes are allowed —
 * the issue call checks the chosen one again.
 */
export default function BatchPurposeField({ recipeId, market, value, onChange, error, touched, size, onMarkets, required = false }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!recipeId) {
      setOptions([]);
      return;
    }
    setLoading(true);
    setFailed(false);
    get_batch_purposes(
      { recipe_id: recipeId, ...(market ? { market } : {}) },
      (rows, markets) => {
        setOptions(rows);
        if (onMarkets) onMarkets(markets);
        setLoading(false);
        // A choice the server no longer allows (record, plant or market changed) is cleared.
        if (value && !rows.some((r) => r.value === value && r.allowed)) onChange(undefined);
      },
      () => {
        setOptions([]);
        setLoading(false);
        setFailed(true);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId, market]);

  const help = touched && error
    ? error
    : failed
      ? 'The purposes for this record could not be read — reload the page.'
      : !recipeId
        ? 'Select a master record first.'
        : '';

  return (
    <Form.Item label="Purpose" required={required} validateStatus={touched && error ? 'error' : ''} help={help}>
      <Select
        size={size}
        value={value || undefined}
        onChange={onChange}
        allowClear
        loading={loading}
        disabled={!recipeId || failed}
        placeholder="What is this batch for?"
        data-testid="batch-purpose-select"
        options={options.map((o) => ({
          value: o.value,
          disabled: !o.allowed,
          label: o.allowed ? o.label : (
            <Tooltip title={o.reason} placement="right">
              <Text type="secondary">{o.label}</Text>
            </Tooltip>
          ),
        }))}
      />
    </Form.Item>
  );
}
